// Vendor registry for the Provisioning page.
//
// This one file drives the whole feature end-to-end: the vendor tile grid, the
// wizard's form fields, the read-only "fixed by server config" summary on the
// confirm step, and which values the result screen shows. Adding a vendor = an
// entry here + a server route at `endpoint`. No page code changes.
//
// Field/result shapes are deliberately plain data (no functions, no JSX) so the
// registry stays serialisable and readable — `pattern` is a source string the
// page compiles with `new RegExp(...)`.
//
// `color` values come from the same palette as config/productColors.js, so a
// vendor's dot here is the same hue as its series in every chart.

export const PROVISIONING_VENDORS = [
  {
    id: 'cove',                          // stable key; also the history row's `vendor` value
    label: 'Cove Data Protection',
    shortLabel: 'Cove',                  // tight spots — table cells, badges
    vendorName: 'N-able',
    description: 'Creates a Cove partner under Israel Region and its first admin console user.',
    color: '#a855f7',
    status: 'live',                      // 'live' | 'coming-soon'
    endpoint: '/api/provisioning/cove',  // POST target (Phase B)
    consoleUrl: 'https://backup.management',

    // Fixed server-side from env. Shown READ-ONLY so the operator can see exactly
    // what will be sent, but never editable from the browser.
    fixed: [
      { label: 'Level', value: 'Service Organization' },
      { label: 'Parent partner', value: 'Israel Region' },
      { label: 'Service type', value: 'All-inclusive' },
      { label: 'Country', value: 'Israel (IL)' },
      { label: 'Data region', value: 'Germany' },
      { label: 'Admin role', value: 'SuperUser + Security Officer' },
    ],

    fields: [
      {
        name: 'clientName',
        label: 'Client name',
        type: 'text',
        required: true,
        maxLength: 128,
        pattern: '^[A-Za-z0-9][A-Za-z0-9 ._&()\\-]{1,127}$',
        patternMessage: 'Letters, numbers, spaces and . _ & ( ) - only.',
        help: 'Must be unique across the entire Cove account tree, not just Israel Region. Cannot be changed later.',
      },
      {
        name: 'adminEmail',
        label: 'Admin email',
        type: 'email',
        required: true,
        help: 'Becomes the login name for the Cove console. N-able emails this address directly.',
      },
    ],

    // Drives the result screen, in order. `copy` adds a copy button; `mono`
    // renders the value in a monospace face.
    result: [
      { key: 'partnerId', label: 'Cove partner ID', copy: true, mono: true },
      { key: 'clientName', label: 'Client name' },
      { key: 'adminEmail', label: 'Admin user', copy: true },
      { key: 'consoleUrl', label: 'Console', type: 'link' },
    ],

    // Cove really does half-succeed: AddPartner can pass and AddUser fail,
    // leaving a partner with no admin. This is the most important string on
    // the page — re-running would collide with the now-taken name.
    incomplete: {
      status: 'partial',
      heading: 'Partner created — admin user failed',
      missingLabel: 'Admin user',
      hint:
        'The partner account exists in Cove, but the admin user was not created. '
        + 'Do NOT re-run provisioning — the client name is now taken. Create the admin '
        + 'user manually in the Cove console, or retry just the user step.',
      action: { label: 'Retry admin user only', path: 'retry-user' },
      rowLabel: 'needs admin user',
    },

    sendsAdminEmail: true,
    confirmWarning: 'Creates a real, billable partner account on N-able Cove.',
  },

  {
    id: 'ncentral',
    label: 'N-central',
    shortLabel: 'N-central',
    vendorName: 'N-able',
    description: 'Creates a customer and its first admin user, with a generated password.',
    color: '#f97316',
    status: 'live',
    endpoint: '/api/provisioning/ncentral',
    consoleUrl: 'https://ncod149.n-able.com',

    fixed: [
      { label: 'Parent org', value: '010 Data Protection Services' },
      { label: 'Admin role', value: 'Default Administrator Role' },
      { label: 'Access group', value: 'Default_136_AG' },
      { label: 'Login', value: 'The admin email address' },
    ],

    fields: [
      {
        name: 'clientName', label: 'Customer name', type: 'text', required: true,
        maxLength: 128,
        help: "N-central does not enforce unique names, so double-check this — creating it twice makes two separate customers.",
      },
      {
        name: 'adminEmail', label: 'Admin email', type: 'email', required: true,
        help: 'This is also the login — N-central has no separate username field.',
      },
      {
        name: 'firstName', label: 'First name', type: 'text', required: false,
        optionalHint: 'defaults to Admin', maxLength: 64,
      },
      {
        name: 'lastName', label: 'Last name', type: 'text', required: false,
        optionalHint: 'defaults to User', maxLength: 64,
      },
    ],

    result: [
      { key: 'partnerId', label: 'Customer ID', copy: true, mono: true },
      { key: 'clientName', label: 'Customer name' },
      { key: 'adminEmail', label: 'Admin user', copy: true },
      // N-central has no activation email and no password-reset API, so this
      // string is the only way into the account. Shown once, never stored.
      {
        key: 'initialPassword', label: 'Initial password', copy: true, mono: true,
        secret: true, oneTime: true,
      },
      { key: 'consoleUrl', label: 'Console', type: 'link' },
    ],

    incomplete: {
      status: 'partial',
      heading: 'Customer created — admin user failed',
      missingLabel: 'Admin user',
      hint:
        'The customer exists in N-central, but the admin user was not created so nobody '
        + 'can log in yet. Retrying creates the user with a NEW password — the first one '
        + 'was never stored and cannot be recovered.',
      action: { label: 'Retry admin user', path: 'retry-ncentral-user' },
      rowLabel: 'needs admin user',
    },

    // N-central sends NOTHING — no activation email, no password reset. That is
    // why the generated password is surfaced once and gated behind an
    // acknowledgement. Do not set this true.
    sendsAdminEmail: false,
    confirmWarning:
      'Creates a real customer and admin user. The password is shown once and cannot be recovered.',
  },
  {
    id: 'keepit',
    label: 'Keepit',
    shortLabel: 'Keepit',
    vendorName: 'Keepit',
    description: 'Creates a sub-account and delegates the product into its portfolio.',
    color: '#f59e0b',
    status: 'live',
    endpoint: '/api/provisioning/keepit',
    consoleUrl: 'https://dk-co.keepit.com',
    // Keepit owns the new account's password, so there is no one-time secret
    // here and nothing for the operator to copy before closing — the client
    // sets their own password from the email Keepit sends them. Confirmed
    // received on the first live run.
    sendsAdminEmail: true,

    fixed: [
      // The id, not a friendly name — the account it points at has no confirmed
      // display name. Value matches docs/PROVISIONING.md and .env.example.
      { label: 'Parent account', value: 'i30wf6-lkfx03-blhpns' },
      { label: 'Account type', value: 'PartnerParent (Partner permissions)' },
      { label: 'Product', value: 'Keepit for 010 Cloud computing Adv' },
      { label: 'Language', value: 'English (en)' },
      { label: 'Login', value: 'The client email address' },
    ],

    fields: [
      {
        name: 'clientName', label: 'Client name', type: 'text', required: true,
        maxLength: 128,
        help: "The company name for the new sub-account.",
      },
      {
        name: 'clientEmail', label: 'Client email', type: 'email', required: true,
        help: 'This is also the login for the account\u2019s first user — Keepit has no separate username.',
      },
    ],

    result: [
      { key: 'partnerId', label: 'Account ID', copy: true, mono: true },
      { key: 'clientName', label: 'Client name' },
      { key: 'adminEmail', label: 'Login', copy: true },
      { key: 'consoleUrl', label: 'Console', type: 'link' },
    ],

    incomplete: {
      status: 'partial',
      heading: 'Account created — product not delegated',
      missingLabel: 'Product',
      hint:
        'The sub-account exists in Keepit, but no product was delegated into its portfolio, '
        + 'so it cannot back anything up yet. Retrying only attaches the product — it will '
        + 'not create a second account.',
      action: { label: 'Retry delegation', path: 'retry-keepit-delegation' },
      rowLabel: 'needs product',
    },

    confirmWarning: 'Creates a real sub-account under the parent partner account.',
  },

  {
    id: 'perceptionpoint',
    label: 'Perception Point',
    shortLabel: 'Perception Point',
    vendorName: 'Perception Point',
    description: 'Creates an organization for mail protection under the MSP org.',
    color: '#eab308',
    status: 'live',
    endpoint: '/api/provisioning/perceptionpoint',
    consoleUrl: 'https://xray.perception-point.io',
    // Unknown whether Perception Point emails the new admin — the script and the
    // reference docs are both silent. Left false until observed on a real run,
    // rather than claiming an email that may never arrive.
    sendsAdminEmail: false,

    fixed: [
      { label: 'Parent org', value: '274276 (010 MSP org)' },
      { label: 'Org type', value: 'MSSP' },
      { label: 'State', value: 'Active' },
      { label: 'Reports', value: 'Weekly / monthly / quarterly off' },
      { label: 'Admin contact', value: 'The admin email address' },
    ],

    fields: [
      {
        name: 'clientName', label: 'Organization name', type: 'text', required: true,
        maxLength: 128,
        help: 'The name for the new organization in Perception Point.',
      },
      {
        name: 'adminEmail', label: 'Admin email', type: 'email', required: true,
        help: "Used for both the org's escalation contact and its owner admin user.",
      },
    ],

    result: [
      { key: 'partnerId', label: 'Organization ID', copy: true, mono: true },
      { key: 'clientName', label: 'Organization name' },
      { key: 'adminEmail', label: 'Admin contact', copy: true },
      { key: 'consoleUrl', label: 'Console', type: 'link' },
    ],

    // No `incomplete` block: this vendor is a SINGLE call, so it cannot
    // half-succeed. There is nothing to retry and no completion route.

    confirmWarning: 'Creates a real organization under the MSP parent org.',
  },

  // Not built yet — a tile only, so the roadmap stays visible. It gains `fields`,
  // `fixed` and `result` when its server route lands.
  {
    id: 'keeper',
    label: 'Keeper Security',
    shortLabel: 'Keeper',
    vendorName: 'Keeper',
    description: 'Creates the account, then converts it to paid once the client activates.',
    color: '#22c55e',
    status: 'live',
    endpoint: '/api/provisioning/keeper',
    consoleUrl: 'https://keepersecurity.eu/console',

    fixed: [
      { label: 'Region', value: 'EU (keepersecurity.eu)' },
      { label: 'Vendor account', value: 'cloud_computing_010' },
      { label: 'Initial billing', value: 'Trial — until converted' },
    ],

    fields: [
      {
        name: 'clientName', label: 'Company name', type: 'text', required: true,
        maxLength: 128,
        help: 'The company name for the new Keeper account.',
      },
      {
        name: 'adminEmail', label: 'Admin email', type: 'email', required: true,
        help: 'Keeper emails this address an activation invite as soon as the account is created.',
      },
      {
        name: 'country', label: 'Country', type: 'text', required: true,
        defaultValue: 'IL', maxLength: 2,
        pattern: '^[A-Za-z]{2}$',
        patternMessage: 'Must be a two-letter ISO country code.',
        help: 'Two-letter ISO country code. Required by Keeper.',
      },
      {
        name: 'zipCode', label: 'Zip / postal code', type: 'text', required: true,
        maxLength: 20,
        help: "The client's postal code. Required by Keeper.",
      },
      {
        name: 'vendorInternalId', label: 'Vendor internal ID', type: 'text', required: false,
        optionalHint: 'leave blank to generate one',
        maxLength: 52,
        pattern: '^[A-Za-z0-9._-]{1,52}$',
        patternMessage: 'Letters, numbers, dot, underscore and hyphen only.',
        help: 'Our own reference for this account. Keeper uses it for every future call, so a stable ID from your own records is preferable — leave blank and one is generated from the company name.',
      },
    ],

    result: [
      { key: 'vendorInternalId', label: 'Vendor internal ID', copy: true, mono: true },
      { key: 'partnerId', label: 'Keeper partner ID', copy: true, mono: true },
      { key: 'clientName', label: 'Company name' },
      { key: 'adminEmail', label: 'Admin user', copy: true },
      { key: 'consoleUrl', label: 'Console', type: 'link' },
    ],

    // Keeper cannot be completed in one pass: a human has to click Keeper's
    // activation email before billing can be switched on, and there is NO API
    // to detect that. So this state is normal and expected, not a failure.
    incomplete: {
      status: 'pending_activation',
      heading: 'Account created — waiting for activation',
      missingLabel: 'Paid billing',
      hint:
        'The account exists and is live, but is still on trial billing. Keeper has emailed '
        + 'the admin an activation invite — once they have clicked it, use Convert to paid. '
        + 'There is no way to check activation, so converting is a probe: "not activated yet" '
        + 'is a normal answer, just try again later.',
      action: { label: 'Convert to paid', path: 'convert-to-paid' },
      rowLabel: 'client must accept the invite',
      // Deletion is one-way gated: this only works BEFORE the admin activates.
      removable: true,
      removeLabel: 'Delete (before activation)',
      removeWarning:
        'This only works while the admin has not yet activated the account. Once they '
        + 'click their invite it can no longer be deleted through the API at all — that '
        + 'needs a Keeper support ticket.',
    },

    sendsAdminEmail: true,
    confirmWarning:
      'Creates a real account and emails the admin an invite. Once activated it cannot be deleted via the API.',
  },
  {
    id: 'sentinelone',
    label: 'SentinelOne',
    shortLabel: 'S1',
    vendorName: 'SentinelOne',
    description: 'Not yet researched — no script exists.',
    color: '#2563eb',
    status: 'coming-soon',
  },
];

export const vendorById = (id) => PROVISIONING_VENDORS.find((v) => v.id === id) || null;

export const liveVendors = () => PROVISIONING_VENDORS.filter((v) => v.status === 'live');

// Short label for a vendor id, falling back to the raw id for historical rows
// whose vendor has since been renamed or removed from the registry.
export const vendorLabel = (id) => vendorById(id)?.shortLabel || id || '—';
